(() => {
  "use strict";

  /*
   * Shopify Direct JavaScript version
   * ---------------------------------
   * This version removes the PHP api.php dependency.
   *
   * REQUIRED:
   * 1. window.SHOPIFY_RECHARGE_CONFIG.rechargeToken
   * 2. Logged-in Shopify customer email injected by Liquid:
   *      window.SHOPIFY_CUSTOMER_EMAIL = {{ customer.email | json }};
   * 3. Browser CORS must be allowed by Recharge and Bitrouter.
   *
   * WARNING:
   * This is intentionally a browser-side implementation. The Recharge
   * private token is therefore exposed to the browser. This is only suitable
   * if the store owner explicitly accepts that trade-off.
   */

  const CFG = window.SHOPIFY_RECHARGE_CONFIG || {};
  const MIDDLEWARE_URL = String(CFG.middlewareUrl || "https://slateblue-antelope-951325.hostingersite.com/zapper-device-id-verify-from-shopify/dashboard-middleware.php").replace(/\/+$/, "");
  const MAX_DEVICES = Number(CFG.maxDevices || 8);
  const PROPERTY_PREFIX = String(CFG.devicePropertyPrefix || "device_id");

  let customerEmail = "";
  let subscriptions = [];

  const $ = (id) => document.getElementById(id);

  document.addEventListener("DOMContentLoaded", init);

  async function init() {
    customerEmail = String(
      window.SHOPIFY_CUSTOMER_EMAIL ||
      window.__SHOPIFY_CUSTOMER_EMAIL__ ||
      ""
    ).trim().toLowerCase();

    if (!customerEmail) {
      showLoginRequired();
      return;
    }

    const logout = $("logout");
    if (logout) {
      logout.addEventListener("click", () => {
        window.location.href = "/account/logout";
      });
      logout.classList.remove("hidden");
    }

    const emailEl = $("customerEmail");
    if (emailEl) emailEl.textContent = customerEmail;

    try {
      await loadDashboard();
    } catch (e) {
      setMessage("dashboardMsg", e.message || "Unable to load subscriptions.", "error");
    }
  }

  async function loadDashboard() {
    toggle("login", false);
    toggle("dashboard", true);

    setMessage("dashboardMsg", "Loading subscriptions...");

    const customer = await rechargeGet(
      "/customers?email=" + encodeURIComponent(customerEmail)
    );

    const customers = Array.isArray(customer.customers) ? customer.customers : [];
    if (!customers.length) {
      throw new Error("No Recharge customer was found for this Shopify customer email.");
    }

    const rechargeCustomer = customers[0];

    const result = await rechargeGet(
      "/subscriptions?customer_id=" + encodeURIComponent(rechargeCustomer.id) + "&limit=250"
    );

    const all = Array.isArray(result.subscriptions) ? result.subscriptions : [];

    subscriptions = all
      .map(normalizeSubscription)
      .filter(isEligibleSubscription);

    setMessage("dashboardMsg", "");

    render();
  }

  function normalizeSubscription(sub) {
    const copy = Object.assign({}, sub);

    copy._sku = getSubscriptionSku(sub);
    copy._device_type = getDeviceType(sub, copy._sku);
    copy._device_limit = copy._device_type === "single" || copy._device_type === "dual"
      ? 1
      : MAX_DEVICES;

    return copy;
  }

  function getSubscriptionSku(sub) {
    const candidates = [
      sub.sku,
      sub.subscription_sku,
      sub.product_sku,
      sub.variant_sku,
      sub.product_code,
      sub.product && sub.product.sku,
      sub.variant && sub.variant.sku
    ];

    for (const value of candidates) {
      if (value) return String(value).trim();
    }

    const title = String(sub.product_title || sub.title || "").toLowerCase();

    // Fallback only when Recharge does not return an SKU field.
    if (title.includes("single tuner")) return "ZXAS-ST";
    if (title.includes("dual tuner")) return "ZXAS-DT";
    if (title.includes("whole home") || title.includes("whole-home")) return "ZXAS-WH";

    return "";
  }

  function getDeviceType(sub, sku) {
    const s = String(sku || "").toUpperCase();
    const title = String(sub.product_title || sub.title || "").toLowerCase();

    if (/-ST$/.test(s) || /_ST$/.test(s) || title.includes("single tuner")) {
      return "single";
    }

    if (/-DT$/.test(s) || /_DT$/.test(s) || title.includes("dual tuner")) {
      return "dual";
    }

    if (/-WH$/.test(s) || /_WH$/.test(s) ||
        title.includes("whole home") || title.includes("whole-home")) {
      return "whole";
    }

    // Preserve the original dashboard's default behavior for unknown types.
    return "whole";
  }

  function isEligibleSubscription(sub) {
    return ["single", "dual", "whole"].includes(sub._device_type);
  }

  function render() {
    const box = $("subscriptions");
    if (!box) return;

    box.innerHTML = "";

    if (!subscriptions.length) {
      box.innerHTML = `
        <div class="card empty">
          <h2>No eligible subscription found</h2>
          <p class="muted">
            We could not find a Single Tuner, Dual Tuner, or Whole Home subscription
            for this customer.
          </p>
        </div>`;
      return;
    }

    subscriptions.forEach((sub) => box.appendChild(makeSubscription(sub)));
  }

  function makeSubscription(sub) {
    const card = document.createElement("div");
    card.className = "card subscription";

    const devices = extractDevices(sub.properties || []);
    const deviceLimit = Number(sub._device_limit || MAX_DEVICES);

    const typeLabel =
      sub._device_type === "single" ? "Single Tuner" :
      sub._device_type === "dual" ? "Dual Tuner" :
      "Whole Home";

    card.innerHTML = `
      <div class="sub-head">
        <div>
          <h2>${esc(sub.product_title || typeLabel + " Subscription")}</h2>
          <span class="status">${esc(sub.status || "ACTIVE")}</span>
        </div>
        <small>Subscription #${esc(sub.id)}</small>
      </div>

      <div class="intro">
        <h3>Your Managed Device ID</h3>
        <p class="muted">
          ${deviceLimit === 1
            ? "This subscription allows one Device ID."
            : "These Device IDs are currently saved on your subscription. You can edit, remove, or add devices."}
        </p>
      </div>

      <div class="devices">
        <div class="device-title">
          <strong>${esc(typeLabel)} Devices</strong>
          <span class="count">${devices.length}/${deviceLimit}</span>
        </div>

        <div class="device-list"></div>

        ${deviceLimit > 1 ? '<button class="btn add" type="button">+ Add Device ID</button>' : ""}

        <div class="save-row">
          <span class="inline"></span>
          <button class="btn primary save" type="button">Save Changes</button>
        </div>
      </div>`;

    const list = card.querySelector(".device-list");

    if (deviceLimit === 1 && devices.length === 0) {
      addRow(list, "", 1);
    } else {
      devices.forEach((value, index) => addRow(list, value, index + 1));
    }

    const addButton = card.querySelector(".add");

    if (addButton) {
      addButton.addEventListener("click", () => {
        const count = list.querySelectorAll(".row").length;
        if (count >= deviceLimit) return;

        addRow(list, "", count + 1);
        refresh(card, deviceLimit);

        const input = list.querySelector(".row:last-child .device-input");
        if (input) input.focus();
      });
    }

    list.addEventListener("input", (event) => {
      if (event.target.classList.contains("device-input")) {
        event.target.value = formatValue(event.target.value);
      }
    });

    list.addEventListener("click", (event) => {
      if (!event.target.classList.contains("remove")) return;

      const row = event.target.closest(".row");
      if (!row) return;

      if (deviceLimit === 1 && list.querySelectorAll(".row").length === 1) {
        list.querySelector(".device-input").value = "";
      } else {
        row.remove();
        renumber(list);
      }

      refresh(card, deviceLimit);

      const status = card.querySelector(".inline");
      status.textContent = "Device ID removed. Click Save Changes to apply the removal.";
      status.className = "inline success";
    });

    card.querySelector(".save").addEventListener("click", () => {
      save(card, sub, deviceLimit);
    });

    refresh(card, deviceLimit);
    return card;
  }

  function addRow(list, value, number) {
    const row = document.createElement("div");
    row.className = "row";

    row.innerHTML = `
      <div class="row-head">
        <label>Device ID <span class="number">${number}</span></label>
        <span class="managed">MANAGED</span>
      </div>

      <div class="input-row">
        <input
          class="device-input"
          maxlength="14"
          value="${esc(formatValue(value))}"
          placeholder="acdb-da11-1a11"
          autocomplete="off"
          inputmode="text">
        <button type="button" class="remove">Remove</button>
      </div>

      <div class="help">Example: <code>acdb-da11-1a11</code></div>`;

    list.appendChild(row);
  }

  function extractDevices(properties) {
    const map = new Map();

    (Array.isArray(properties) ? properties : []).forEach((property) => {
      const name = String(property.name || "");
      const value = String(property.value || "");

      if (name === PROPERTY_PREFIX) {
        map.set(1, value);
        return;
      }

      const match = name.match(
        new RegExp("^" + escapeRegExp(PROPERTY_PREFIX) + "_(\\d+)$")
      );

      if (match) {
        const number = Number(match[1]);
        if (number >= 2 && number <= MAX_DEVICES) {
          map.set(number, value);
        }
      }
    });

    return [...map.entries()]
      .sort((a, b) => a[0] - b[0])
      .map((entry) => entry[1])
      .filter(Boolean);
  }

  async function save(card, sub, deviceLimit) {
    const status = card.querySelector(".inline");
    const button = card.querySelector(".save");

    const values = [...card.querySelectorAll(".device-input")]
      .map((input) => input.value.trim().toLowerCase())
      .filter(Boolean);

    if (values.length > deviceLimit) {
      status.textContent =
        `Maximum ${deviceLimit} Device ID${deviceLimit === 1 ? "" : "s"} are allowed for this subscription.`;
      status.className = "inline error";
      return;
    }

    if (values.some((value) => !valid(value))) {
      status.textContent =
        "Every Device ID must use acdb-da11-1a11 format.";
      status.className = "inline error";
      return;
    }

    if (new Set(values).size !== values.length) {
      status.textContent = "Duplicate Device IDs are not allowed.";
      status.className = "inline error";
      return;
    }

    button.disabled = true;

    try {
      // A non-empty device list must pass ZapperBox validation first.
      if (values.length) {
        status.textContent = "Verifying Device ID with ZapperBox...";
        status.className = "inline";

        const verification = await verifyWithZapperBox(values, sub);

        const failed = (verification.devices || [])
          .find((device) => device.status !== "PASS");

        if (!verification.success || failed) {
          const reason = failed
            ? `${failed.device_id || "Device ID"}: ${failed.reason || failed.status || "Device verification failed."}`
            : "Device verification failed.";

          status.textContent = reason;
          status.className = "inline error";
          return;
        }
      }

      status.textContent = "Updating Recharge subscription...";
      status.className = "inline";

      const properties = values.map((value, index) => ({
        name: index === 0 ? PROPERTY_PREFIX : `${PROPERTY_PREFIX}_${index + 1}`,
        value
      }));

      // Empty properties intentionally clears the managed Device IDs,
      // matching the existing dashboard's removal behavior.
      const updated = await rechargePut(
        `/subscriptions/${encodeURIComponent(sub.id)}`,
        { properties }
      );

      const updatedSubscription = updated.subscription || updated;
      const index = subscriptions.findIndex(
        (item) => String(item.id) === String(sub.id)
      );

      if (index !== -1) {
        subscriptions[index] = normalizeSubscription(updatedSubscription);
      }

      status.textContent = "Device IDs updated successfully.";
      status.className = "inline success";

      // Re-render the card from the Recharge response so the UI reflects
      // exactly what Recharge returned.
      render();
    } catch (error) {
      status.textContent = error.message || "Update failed.";
      status.className = "inline error";
    } finally {
      button.disabled = false;
    }
  }

  async function verifyWithZapperBox(devices, sub) {
    const sku = sub._sku || getSubscriptionSku(sub);

    if (!sku) {
      throw new Error(
        "Subscription SKU could not be determined. Please provide the actual Recharge subscription SKU."
      );
    }

    const response = await fetch(MIDDLEWARE_URL + "/?action=zb_verify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        devices,
        sub_sku: sku,
        sub_id: String(sub.id)
      })
    });

    const data = await parseJsonResponse(response);

    if (!response.ok) {
      throw new Error(
        data.reason ||
        data.error ||
        `ZapperBox verification failed (HTTP ${response.status}).`
      );
    }

    return data;
  }

  async function rechargeGet(path) {
    const url = new URL(MIDDLEWARE_URL + "/", window.location.href);
    const params = new URLSearchParams(path.split("?")[1] || "");
    const resource = path.split("?")[0];

    if (resource === "/customers") {
      url.searchParams.set("action", "customers");
    } else if (resource === "/subscriptions") {
      url.searchParams.set("action", "subscriptions");
    } else {
      throw new Error("Unsupported Recharge GET request.");
    }

    params.forEach((value, key) => url.searchParams.set(key, value));

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: { "Accept": "application/json" }
    });

    return handleRechargeResponse(response);
  }

  async function rechargePut(path, body) {
    const match = path.match(/^\/subscriptions\/(.+)$/);
    if (!match) throw new Error("Unsupported Recharge update request.");

    const url = new URL(MIDDLEWARE_URL + "/", window.location.href);
    url.searchParams.set("action", "subscription_update");
    url.searchParams.set("id", decodeURIComponent(match[1]));

    const response = await fetch(url.toString(), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify(body)
    });

    return handleRechargeResponse(response);
  }

  async function handleRechargeResponse(response) {
    const data = await parseJsonResponse(response);

    if (!response.ok) {
      const message =
        data?.errors?.[0]?.message ||
        data?.error ||
        `Middleware returned HTTP ${response.status}.`;

      throw new Error(message);
    }

    return data;
  }

  async function parseJsonResponse(response) {
    const text = await response.text();

    try {
      return text ? JSON.parse(text) : {};
    } catch (e) {
      throw new Error(
        `API returned a non-JSON response (HTTP ${response.status}).`
      );
    }
  }

  function refresh(card, deviceLimit) {
    const count = card.querySelectorAll(".row").length;
    const countEl = card.querySelector(".count");

    if (countEl) {
      countEl.textContent = `${count}/${deviceLimit}`;
    }

    const addButton = card.querySelector(".add");
    if (addButton) {
      addButton.disabled = count >= deviceLimit;
    }
  }

  function renumber(list) {
    [...list.querySelectorAll(".row")].forEach((row, index) => {
      const number = row.querySelector(".number");
      if (number) number.textContent = index + 1;
    });
  }

  function formatValue(value) {
    const raw = String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")
      .slice(0, 12);

    const first = raw.slice(0, 4);
    const rest = raw.slice(4);

    return first +
      (rest ? `-${rest.slice(0, 4)}` : "") +
      (rest.length > 4 ? `-${rest.slice(4, 8)}` : "");
  }

  function valid(value) {
    return /^[a-z]{4}-[a-z0-9]{4}-[a-z0-9]{4}$/.test(value);
  }

  function showLoginRequired() {
    toggle("login", true);
    toggle("dashboard", false);

    setMessage(
      "emailMsg",
      "Please log in to your Shopify customer account before opening this page.",
      "error"
    );
  }

  function toggle(id, show) {
    const element = $(id);
    if (!element) return;

    element.classList.toggle("hidden", !show);
  }

  function setMessage(id, text, type) {
    const element = $(id);
    if (!element) return;

    element.textContent = text || "";
    element.className = "message" + (type ? ` ${type}` : "");
  }

  function esc(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
})();
